import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const RECENT_SCANS_KEY = 'new-inscan-recent-scans-v1';
const MAX_RECENT_SCANS = 30;
const STATUS_META = {
  success: { label: 'Success', color: '#166534', background: '#dcfce7', border: '#86efac' },
  processing: { label: 'Processing', color: '#854d0e', background: '#fef3c7', border: '#fde68a' },
  error: { label: 'Error', color: '#991b1b', background: '#fee2e2', border: '#fca5a5' },
};

function getCurrentDate() {
  return new Date().toISOString().slice(0, 10);
}

function getCurrentTime() {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
}

function safeReadRecentScans() {
  try {
    const raw = localStorage.getItem(RECENT_SCANS_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function normalizeAwbCandidate(value) {
  if (!value) {
    return '';
  }

  let cleaned = value.trim().toUpperCase();
  if (!cleaned) {
    return '';
  }

  cleaned = cleaned.replace(/[^A-Z0-9-]/g, '');
  if (cleaned.startsWith('EKX') && cleaned.length > 8) {
    cleaned = cleaned.slice(3, -5);
  }

  return cleaned;
}

function isValidAwb(value) {
  return /^[A-Z0-9-]{6,30}$/.test(value) && /\d/.test(value);
}

function parseAwbText(rawValue, autoFormat) {
  if (!rawValue) {
    return { validAwbs: [], invalidEntries: [] };
  }

  const candidates = autoFormat
    ? rawValue.toUpperCase().match(/[A-Z0-9-]{6,40}/g) || []
    : rawValue.split(/[\n,;\t ]+/);

  const validSet = new Set();
  const invalidEntries = [];

  candidates.forEach((candidate) => {
    const normalized = normalizeAwbCandidate(candidate);
    if (!normalized) {
      return;
    }

    if (isValidAwb(normalized)) {
      validSet.add(normalized);
      return;
    }

    invalidEntries.push(candidate.trim());
  });

  return {
    validAwbs: Array.from(validSet),
    invalidEntries,
  };
}

function buildRecentScanEntry({ awbs, homeBase, scanDate, scanTime, requestId }) {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    requestId,
    awbs,
    homeBase,
    scanDate,
    scanTime,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    status: 'processing',
    successCount: 0,
    errorCount: 0,
    summary: 'Scan submitted. Waiting for final status.',
    errorMessages: [],
  };
}

export default function NewInScan() {
  const API_URL = process.env.REACT_APP_API_URL;
  const [homebases, setHomebases] = useState([]);
  const [homebaseLoading, setHomebaseLoading] = useState(false);
  const [scanInfo, setScanInfo] = useState({
    scan_date: getCurrentDate(),
    scan_time: getCurrentTime(),
    home_base: '',
  });
  const [draftValue, setDraftValue] = useState('');
  const [queuedAwbs, setQueuedAwbs] = useState([]);
  const [autoFormat, setAutoFormat] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [recentScans, setRecentScans] = useState(() => safeReadRecentScans());
  const [todayData, setTodayData] = useState(null);
  const [todayLoading, setTodayLoading] = useState(false);
  const [todaySearch, setTodaySearch] = useState('');
  const activePollsRef = useRef({});

  const queuedSummary = useMemo(() => ({
    total: queuedAwbs.length,
    preview: queuedAwbs.slice(0, 6),
  }), [queuedAwbs]);

  useEffect(() => {
    fetchHomebases();
    fetchTodayScans();
  }, []);

  const fetchTodayScans = async () => {
    setTodayLoading(true);
    try {
      const res = await axios.get(`${API_URL}shypvdriverapp/jobs/inScanToday`);
      setTodayData((res && res.data && res.data.data) || null);
    } catch (e) {
      // non-fatal — the list just stays empty
    } finally {
      setTodayLoading(false);
    }
  };

  const todayAwbsFiltered = useMemo(() => {
    const list = (todayData && todayData.awbs) || [];
    const q = todaySearch.trim().toLowerCase();
    return q ? list.filter((a) => a.toLowerCase().includes(q)) : list;
  }, [todayData, todaySearch]);

  useEffect(() => {
    recentScans.forEach((scan) => {
      if (scan.status === 'processing' && scan.requestId && !activePollsRef.current[scan.id]) {
        pollScanStatus(scan.id, scan.requestId);
      }
    });
  }, [recentScans]);

  const persistRecentScans = (updater) => {
    setRecentScans((previous) => {
      const nextValue = typeof updater === 'function' ? updater(previous) : updater;
      const trimmedValue = nextValue.slice(0, MAX_RECENT_SCANS);
      localStorage.setItem(RECENT_SCANS_KEY, JSON.stringify(trimmedValue));
      return trimmedValue;
    });
  };

  const setBanner = (type, nextMessage) => {
    setMessageType(type);
    setMessage(nextMessage);
  };

  const fetchHomebases = async () => {
    setHomebaseLoading(true);
    try {
      const response = await axios.get(`${API_URL}shypvdriverapp/homebase/getHomebases`);
      if (response.data.success) {
        setHomebases(response.data.data || []);
        return;
      }
      setBanner('error', 'Failed to load homebases.');
    } catch (error) {
      setBanner('error', 'Failed to load homebases.');
    } finally {
      setHomebaseLoading(false);
    }
  };

  const updateRecentScan = (scanId, patch) => {
    persistRecentScans((previous) => previous.map((scan) => (
      scan.id === scanId
        ? { ...scan, ...patch, updatedAt: new Date().toISOString() }
        : scan
    )));
  };

  const handlePaste = (event) => {
    if (!autoFormat) {
      return;
    }

    const pastedText = event.clipboardData.getData('text');
    const { validAwbs } = parseAwbText(pastedText, true);
    if (!validAwbs.length) {
      return;
    }

    event.preventDefault();
    setDraftValue((previous) => {
      const parts = [previous.trim(), validAwbs.join('\n')].filter(Boolean);
      return parts.join('\n');
    });
  };

  const addDraftToQueue = () => {
    const { validAwbs, invalidEntries } = parseAwbText(draftValue, autoFormat);
    if (!validAwbs.length) {
      setBanner('error', 'No valid AWBs found in the pasted text.');
      return;
    }

    setQueuedAwbs((previous) => {
      const merged = Array.from(new Set([...previous, ...validAwbs]));
      return merged;
    });
    setDraftValue('');

    if (invalidEntries.length) {
      setBanner('warning', `${validAwbs.length} AWBs added. ${invalidEntries.length} invalid entries were ignored.`);
      return;
    }

    setBanner('success', `${validAwbs.length} AWBs added to the queue.`);
  };

  const removeQueuedAwb = (awb) => {
    setQueuedAwbs((previous) => previous.filter((item) => item !== awb));
  };

  const clearQueue = () => {
    setQueuedAwbs([]);
    setDraftValue('');
    setBanner('info', 'Queue cleared.');
  };

  const pollScanStatus = (scanId, requestId, attempt = 1) => {
    activePollsRef.current[scanId] = true;

    axios
      .get(`${API_URL}shypvdriverapp/jobs/inScanStatus/${requestId}`)
      .then((response) => {
        const apiStatus = String(response.data.STATUS || '').toUpperCase();
        if (apiStatus === 'PENDING' || apiStatus === 'QUEUED') {
          updateRecentScan(scanId, {
            status: 'processing',
            summary: `Scan request is ${apiStatus.toLowerCase()}.`,
          });

          if (attempt >= 45) {
            updateRecentScan(scanId, {
              status: 'error',
              summary: 'Timed out while checking the scan status.',
              errorMessages: ['Timed out while checking the scan status.'],
            });
            delete activePollsRef.current[scanId];
            return;
          }

          setTimeout(() => pollScanStatus(scanId, requestId, attempt + 1), 3000);
          return;
        }

        const payload = response.data.data || {};
        const awbs = String(payload.awbs || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        const errors = Array.isArray(payload.errors) ? payload.errors : [];
        const errorMessages = errors.map((item) => item.errorMessage || 'Unknown error');
        const errorAwbSet = new Set();

        errors.forEach((errorItem) => {
          const errorDetails = Array.isArray(errorItem.errorDetails)
            ? errorItem.errorDetails
            : [errorItem.errorDetails];
          errorDetails.filter(Boolean).forEach((detail) => {
            const parsed = parseAwbText(String(detail), true).validAwbs;
            parsed.forEach((awb) => errorAwbSet.add(awb));
          });
        });

        const successCount = awbs.filter((awb) => !errorAwbSet.has(awb)).length;
        const errorCount = awbs.length - successCount;
        const isSuccessful = errorCount === 0;

        updateRecentScan(scanId, {
          status: isSuccessful ? 'success' : 'error',
          successCount,
          errorCount,
          errorMessages,
          summary: isSuccessful
            ? `${successCount} AWBs completed successfully.`
            : `${successCount} successful, ${errorCount} failed.`,
        });

        delete activePollsRef.current[scanId];
      })
      .catch(() => {
        updateRecentScan(scanId, {
          status: 'error',
          summary: 'Could not fetch the latest scan status.',
          errorMessages: ['Could not fetch the latest scan status.'],
        });
        delete activePollsRef.current[scanId];
      });
  };

  const submitInscan = async () => {
    if (!scanInfo.scan_date || !scanInfo.scan_time || !scanInfo.home_base) {
      setBanner('error', 'Date, time, and homebase are required.');
      return;
    }

    if (!queuedAwbs.length) {
      setBanner('error', 'Add at least one valid AWB before submitting.');
      return;
    }

    setSubmitting(true);
    setBanner('info', 'Submitting new inscan request...');

    const payload = {
      scan_date: scanInfo.scan_date,
      scan_time: scanInfo.scan_time,
      home_base: scanInfo.home_base,
      status: 'inscan',
      driverId: scanInfo.driver,
      updated_by: sessionStorage.getItem('userid'),
      awbs: queuedAwbs,
    };

    try {
      const response = await axios.post(`${API_URL}shypvdriverapp/jobs/inScanShipmentsNew`, payload);
      const requestId = response.data?.data?.requestId;
      if (!requestId) {
        setBanner('error', 'Scan request was submitted but no request ID was returned.');
        setSubmitting(false);
        return;
      }

      const newEntry = buildRecentScanEntry({
        awbs: queuedAwbs,
        homeBase: scanInfo.home_base,
        scanDate: scanInfo.scan_date,
        scanTime: scanInfo.scan_time,
        requestId,
      });

      persistRecentScans((previous) => [newEntry, ...previous]);
      setBanner('success', `Submitted ${queuedAwbs.length} AWBs. Request ID: ${requestId}`);
      setQueuedAwbs([]);
      setDraftValue('');
      pollScanStatus(newEntry.id, requestId);
      fetchTodayScans();
    } catch (error) {
      setBanner('error', 'Failed to submit the new inscan request.');
    } finally {
      setSubmitting(false);
    }
  };

  const recentCards = useMemo(() => recentScans.map((scan) => {
    const meta = STATUS_META[scan.status] || STATUS_META.processing;
    return { ...scan, meta };
  }), [recentScans]);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>New Inscan</h2>
          <p style={styles.subtitle}>
            Modern inscan flow with paste cleanup, queue validation, and persistent recent inscan history.
          </p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.secondaryButton} onClick={fetchHomebases} disabled={homebaseLoading}>
            {homebaseLoading ? 'Refreshing...' : 'Refresh Homebases'}
          </button>
        </div>
      </div>

      {message ? (
        <div
          style={{
            ...styles.banner,
            ...(messageType === 'error'
              ? styles.bannerError
              : messageType === 'success'
                ? styles.bannerSuccess
                : messageType === 'warning'
                  ? styles.bannerWarning
                  : styles.bannerInfo),
          }}
        >
          <span>{message}</span>
          <button style={styles.bannerClose} onClick={() => setMessage('')}>
            x
          </button>
        </div>
      ) : null}

      <div style={styles.layout}>
        <div style={styles.leftColumn}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>Scan Setup</div>
            <div style={styles.cardBody}>
              <div style={styles.formGrid}>
                <label style={styles.field}>
                  <span style={styles.label}>Scan Date</span>
                  <input
                    type="date"
                    value={scanInfo.scan_date}
                    onChange={(event) => setScanInfo({ ...scanInfo, scan_date: event.target.value })}
                    style={styles.input}
                  />
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Scan Time</span>
                  <input
                    type="time"
                    value={scanInfo.scan_time}
                    onChange={(event) => setScanInfo({ ...scanInfo, scan_time: event.target.value })}
                    style={styles.input}
                  />
                </label>
                <label style={styles.fieldFull}>
                  <span style={styles.label}>Homebase</span>
                  <select
                    value={scanInfo.home_base}
                    onChange={(event) => setScanInfo({ ...scanInfo, home_base: event.target.value })}
                    style={styles.input}
                  >
                    <option value="">Select a homebase</option>
                    {homebases.map((homebase) => (
                      <option key={homebase.homebaseCode} value={homebase.homebaseCode}>
                        {homebase.homebaseName}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}>Paste AWBs</div>
            <div style={styles.cardBody}>
              <div style={styles.toggleRow}>
                <div>
                  <div style={styles.toggleTitle}>Auto reformat pasted text</div>
                  <div style={styles.helperText}>
                    When enabled, pasted text is cleaned and only valid AWB-like values are kept.
                  </div>
                </div>
                <label style={styles.switch}>
                  <input
                    type="checkbox"
                    checked={autoFormat}
                    onChange={(event) => setAutoFormat(event.target.checked)}
                    style={styles.switchInput}
                  />
                  <span style={{ ...styles.slider, ...(autoFormat ? styles.sliderActive : styles.sliderInactive) }}>
                    <span style={{ ...styles.sliderKnob, ...(autoFormat ? styles.sliderKnobActive : styles.sliderKnobInactive) }} />
                  </span>
                </label>
              </div>

              <textarea
                value={draftValue}
                onChange={(event) => setDraftValue(event.target.value)}
                onPaste={handlePaste}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    addDraftToQueue();
                  }
                }}
                placeholder="Paste or scan AWBs here. Press Enter to add them to the queue."
                style={styles.textarea}
              />

              <div style={styles.helperRow}>
                <span style={styles.helperText}>Accepted format: uppercase letters, numbers, hyphen. Minimum 6 characters and must contain digits.</span>
                <div style={styles.actionRow}>
                  <button style={styles.secondaryButton} onClick={() => setDraftValue('')}>
                    Clear Text
                  </button>
                  <button style={styles.primaryButton} onClick={addDraftToQueue}>
                    Add To Queue
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}>Queued AWBs</div>
            <div style={styles.cardBody}>
              <div style={styles.queueSummaryRow}>
                <div>
                  <div style={styles.queueCount}>{queuedSummary.total}</div>
                  <div style={styles.helperText}>ready to submit</div>
                </div>
                <div style={styles.actionRow}>
                  <button style={styles.secondaryButton} onClick={clearQueue} disabled={!queuedAwbs.length}>
                    Clear Queue
                  </button>
                  <button style={styles.submitButton} onClick={submitInscan} disabled={submitting || !queuedAwbs.length}>
                    {submitting ? 'Submitting...' : 'Submit New Inscan'}
                  </button>
                </div>
              </div>

              {queuedAwbs.length ? (
                <div style={styles.awbGrid}>
                  {queuedSummary.preview.map((awb) => (
                    <div key={awb} style={styles.awbChip}>
                      <span style={styles.awbText}>{awb}</span>
                      <button style={styles.awbRemove} onClick={() => removeQueuedAwb(awb)}>
                        x
                      </button>
                    </div>
                  ))}
                  {queuedAwbs.length > queuedSummary.preview.length ? (
                    <div style={styles.moreCount}>+{queuedAwbs.length - queuedSummary.preview.length} more</div>
                  ) : null}
                </div>
              ) : (
                <div style={styles.emptyState}>No AWBs queued yet.</div>
              )}
            </div>
          </div>
        </div>

        <div style={styles.rightColumn}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>Today's Scans</div>
            <div style={styles.cardBody}>
              <div style={styles.recentMetaRow}>
                <span><strong>{(todayData && todayData.distinctAwbs) || 0}</strong> AWBs scanned today · {(todayData && todayData.totalScans) || 0} scan(s)</span>
                <button style={{ ...styles.secondaryButton, padding: '4px 10px' }} onClick={fetchTodayScans} disabled={todayLoading}>
                  {todayLoading ? '…' : 'Refresh'}
                </button>
              </div>
              <input
                placeholder="Search AWB…"
                value={todaySearch}
                onChange={(e) => setTodaySearch(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', margin: '8px 0', borderRadius: 8, border: '1px solid #d8dee9', boxSizing: 'border-box' }}
              />
              {todayAwbsFiltered.length ? (
                <div style={styles.recentAwbPreview}>
                  {todayAwbsFiltered.map((awb) => (
                    <span key={awb} style={styles.recentAwbChip}>{awb}</span>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>
                  {todayLoading ? 'Loading…' : (todaySearch ? 'No match.' : 'Nothing scanned today yet.')}
                </div>
              )}
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}>Recent Inscans</div>
            <div style={styles.cardBody}>
              <div style={styles.helperText}>
                Stored in this browser so they stay available across refreshes and browser restarts.
              </div>
              {recentCards.length ? (
                <div style={styles.recentList}>
                  {recentCards.map((scan) => (
                    <div key={scan.id} style={{ ...styles.recentItem, borderColor: scan.meta.border }}>
                      <div style={styles.recentHeader}>
                        <div>
                          <div style={styles.recentRequestId}>{scan.requestId}</div>
                          <div style={styles.recentTime}>{new Date(scan.createdAt).toLocaleString()}</div>
                        </div>
                        <span style={{ ...styles.statusBadge, background: scan.meta.background, color: scan.meta.color }}>
                          {scan.meta.label}
                        </span>
                      </div>
                      <div style={styles.recentMetaRow}>
                        <span>{scan.homeBase || 'No homebase'}</span>
                        <span>{scan.awbs.length} AWBs</span>
                      </div>
                      <div style={styles.recentSummary}>{scan.summary}</div>
                      <div style={styles.progressRow}>
                        <span style={styles.successText}>Success: {scan.successCount || 0}</span>
                        <span style={styles.errorText}>Errors: {scan.errorCount || 0}</span>
                      </div>
                      {scan.awbs?.length ? (
                        <div style={styles.recentAwbPreview}>
                          {scan.awbs.slice(0, 5).map((awb) => (
                            <span key={`${scan.id}-${awb}`} style={styles.recentAwbChip}>{awb}</span>
                          ))}
                          {scan.awbs.length > 5 ? <span style={styles.recentAwbChip}>+{scan.awbs.length - 5}</span> : null}
                        </div>
                      ) : null}
                      {scan.errorMessages?.length ? (
                        <div style={styles.errorList}>
                          {scan.errorMessages.slice(0, 2).map((errorMessage, index) => (
                            <div key={`${scan.id}-error-${index}`} style={styles.errorListItem}>{errorMessage}</div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.emptyState}>No recent inscans yet.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    padding: '24px',
    background: '#f5f7fb',
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: 700,
    color: '#102a43',
  },
  subtitle: {
    margin: '8px 0 0',
    color: '#486581',
    fontSize: '14px',
  },
  headerActions: {
    display: 'flex',
    gap: '10px',
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.6fr) minmax(320px, 0.9fr)',
    gap: '20px',
    alignItems: 'start',
  },
  leftColumn: {
    display: 'grid',
    gap: '20px',
  },
  rightColumn: {
    display: 'grid',
    gap: '20px',
    position: 'sticky',
    top: '20px',
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 14px 30px rgba(15, 23, 42, 0.07)',
    border: '1px solid #e6edf5',
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '16px 18px',
    borderBottom: '1px solid #eef2f7',
    fontWeight: 700,
    fontSize: '15px',
    color: '#102a43',
    background: '#fbfdff',
  },
  cardBody: {
    padding: '18px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '14px',
  },
  field: {
    display: 'grid',
    gap: '8px',
  },
  fieldFull: {
    display: 'grid',
    gap: '8px',
    gridColumn: '1 / -1',
  },
  label: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#486581',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  input: {
    height: '44px',
    borderRadius: '12px',
    border: '1px solid #d9e2ec',
    padding: '0 14px',
    fontSize: '14px',
    color: '#102a43',
    outline: 'none',
    background: '#ffffff',
  },
  textarea: {
    width: '100%',
    minHeight: '220px',
    resize: 'vertical',
    borderRadius: '14px',
    border: '1px solid #d9e2ec',
    padding: '14px',
    fontSize: '14px',
    fontFamily: 'Menlo, Monaco, Consolas, monospace',
    lineHeight: 1.45,
    outline: 'none',
    boxSizing: 'border-box',
    marginTop: '14px',
  },
  toggleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    alignItems: 'center',
  },
  toggleTitle: {
    fontSize: '14px',
    fontWeight: 700,
    color: '#102a43',
  },
  helperText: {
    marginTop: '4px',
    fontSize: '12px',
    color: '#7b8794',
  },
  helperRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '14px',
    marginTop: '14px',
    flexWrap: 'wrap',
  },
  actionRow: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  primaryButton: {
    height: '42px',
    padding: '0 16px',
    borderRadius: '12px',
    border: 'none',
    background: '#1565d8',
    color: '#ffffff',
    fontWeight: 700,
    cursor: 'pointer',
  },
  secondaryButton: {
    height: '42px',
    padding: '0 16px',
    borderRadius: '12px',
    border: '1px solid #d9e2ec',
    background: '#ffffff',
    color: '#334e68',
    fontWeight: 700,
    cursor: 'pointer',
  },
  submitButton: {
    height: '44px',
    padding: '0 18px',
    borderRadius: '12px',
    border: 'none',
    background: 'linear-gradient(135deg, #0ea5e9, #2563eb)',
    color: '#ffffff',
    fontWeight: 700,
    cursor: 'pointer',
  },
  queueSummaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '14px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  queueCount: {
    fontSize: '30px',
    fontWeight: 800,
    color: '#102a43',
  },
  awbGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  awbChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '999px',
    padding: '8px 12px',
  },
  awbText: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#1d4ed8',
    fontFamily: 'Menlo, Monaco, Consolas, monospace',
  },
  awbRemove: {
    border: 'none',
    background: 'transparent',
    color: '#b91c1c',
    cursor: 'pointer',
    fontWeight: 700,
  },
  moreCount: {
    alignSelf: 'center',
    fontSize: '12px',
    color: '#486581',
    fontWeight: 700,
  },
  emptyState: {
    border: '1px dashed #cbd2d9',
    borderRadius: '14px',
    padding: '24px',
    textAlign: 'center',
    color: '#7b8794',
    background: '#f8fbff',
  },
  banner: {
    marginBottom: '18px',
    borderRadius: '14px',
    padding: '14px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
    border: '1px solid transparent',
  },
  bannerSuccess: {
    background: '#ecfdf3',
    borderColor: '#a7f3d0',
    color: '#166534',
  },
  bannerError: {
    background: '#fff1f2',
    borderColor: '#fecdd3',
    color: '#b42318',
  },
  bannerWarning: {
    background: '#fffbeb',
    borderColor: '#fde68a',
    color: '#854d0e',
  },
  bannerInfo: {
    background: '#eff6ff',
    borderColor: '#bfdbfe',
    color: '#1d4ed8',
  },
  bannerClose: {
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
    fontWeight: 700,
  },
  recentList: {
    display: 'grid',
    gap: '14px',
    marginTop: '14px',
  },
  recentItem: {
    border: '1px solid #d9e2ec',
    borderLeftWidth: '5px',
    borderRadius: '14px',
    padding: '14px',
    background: '#ffffff',
  },
  recentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'flex-start',
  },
  recentRequestId: {
    fontWeight: 800,
    color: '#102a43',
    fontSize: '13px',
    fontFamily: 'Menlo, Monaco, Consolas, monospace',
  },
  recentTime: {
    marginTop: '4px',
    fontSize: '11px',
    color: '#7b8794',
  },
  statusBadge: {
    padding: '5px 10px',
    borderRadius: '999px',
    fontSize: '11px',
    fontWeight: 800,
  },
  recentMetaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
    marginTop: '12px',
    fontSize: '12px',
    color: '#486581',
  },
  recentSummary: {
    marginTop: '10px',
    fontSize: '13px',
    color: '#243b53',
  },
  progressRow: {
    display: 'flex',
    gap: '12px',
    marginTop: '10px',
    fontSize: '12px',
    fontWeight: 700,
  },
  successText: {
    color: '#166534',
  },
  errorText: {
    color: '#991b1b',
  },
  recentAwbPreview: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginTop: '12px',
  },
  recentAwbChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '5px 9px',
    borderRadius: '999px',
    background: '#f0f4f8',
    color: '#334e68',
    fontSize: '11px',
    fontWeight: 700,
    fontFamily: 'Menlo, Monaco, Consolas, monospace',
  },
  errorList: {
    display: 'grid',
    gap: '6px',
    marginTop: '12px',
  },
  errorListItem: {
    fontSize: '12px',
    color: '#b42318',
    background: '#fff5f5',
    borderRadius: '10px',
    padding: '8px 10px',
  },
  switch: {
    position: 'relative',
    display: 'inline-block',
    width: '52px',
    height: '30px',
  },
  switchInput: {
    opacity: 0,
    width: 0,
    height: 0,
    position: 'absolute',
  },
  slider: {
    position: 'absolute',
    inset: 0,
    cursor: 'pointer',
    transition: '.2s',
    borderRadius: '999px',
  },
  sliderActive: {
    backgroundColor: '#2563eb',
  },
  sliderInactive: {
    backgroundColor: '#cbd5e1',
  },
  sliderKnob: {
    position: 'absolute',
    top: '3px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    boxShadow: '0 3px 8px rgba(15, 23, 42, 0.18)',
    transition: '.2s',
  },
  sliderKnobActive: {
    left: '25px',
  },
  sliderKnobInactive: {
    left: '3px',
  },
};